import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
} from "@mui/material";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import Title from "./Title";
import validator from "validator";

const SingleMdnChange = ({ title }) => {
  const [oldPhoneNumber, setOldPhoneNumber] = useState("");
  const [zip, setZip] = useState("");
  const [zipOptional, setZipOptional] = useState(false);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState("");
  const { getAccessTokenSilently } = useAuth0();
  const responseRef = useRef(null);
  const [showResponse, setShowResponse] = useState(false);

  const fetchStats = async () => {
    setStatsLoading(true);
    setStatsError("");
    try {
      const token = await getAccessTokenSilently();
      const res = await axios.get("/api/single-mdn-change/stats", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(res.data);
    } catch (err) {
      setStatsError("Failed to load stats");
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (response) {
      setShowResponse(true);
    }
  }, [response]);

  useEffect(() => {
    if (showResponse && responseRef.current) {
      responseRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [showResponse]);

  const handleDownload = async (period, status) => {
    const backendStatus = status === "total" ? "all" : status;
    try {
      const token = await getAccessTokenSilently();
      const res = await axios.get(
        `/api/single-mdn-change/download?period=${period}&status=${backendStatus}`,
        {
          responseType: "blob",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `single_mdn_change_${period}_${backendStatus}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      alert("Download failed or no data available.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowResponse(false); // Hide response before new request
    setLoading(true);
    setError("");
    setResponse(null);
    if (!oldPhoneNumber || (!zip && !zipOptional)) {
      setError(
        "Old phone number is required. Zip is required unless marked optional."
      );
      setLoading(false);
      return;
    }
    // Validate phone: must be 10 or 11 digits, all numeric, use isMobilePhone for US
    let phoneToValidate = oldPhoneNumber;
    if (phoneToValidate.length === 11 && phoneToValidate.startsWith("1")) {
      phoneToValidate = phoneToValidate.substring(1);
    }
    if (!validator.isMobilePhone(phoneToValidate, "en-US")) {
      setError(
        "Phone number must be a valid US mobile number (10 or 11 digits)."
      );
      setLoading(false);
      return;
    }
    // Validate zip if not optional: must be 5 digits, all numeric
    if (!zipOptional && (!validator.isNumeric(zip) || zip.length !== 5)) {
      setError("Zip code must be 5 digits and numeric.");
      setLoading(false);
      return;
    }
    try {
      const token = await getAccessTokenSilently();
      const payload = zipOptional
        ? { oldPhoneNumber, zip: null }
        : { oldPhoneNumber, zip };
      const res = await axios.post("/api/single-mdn-change", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResponse(res.data);
      fetchStats();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ marginTop: 4 }}>
      <Title pageTitle={title || "Single MDN Change"} />
      <Paper
        elevation={3}
        sx={{ padding: 4, mb: 4, width: "100%", maxWidth: 1200, mx: "auto" }}
      >
        <Typography variant="h5" gutterBottom align="center">
          Single MDN Change Stats
        </Typography>
        {statsLoading ? (
          <Typography>Loading stats...</Typography>
        ) : statsError ? (
          <Typography color="error">{statsError}</Typography>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "center",
              alignItems: "flex-start",
              gap: 4,
            }}
          >
            {/* This Month */}
            <Box
              sx={{ minWidth: 220, textAlign: "center", mb: { xs: 3, sm: 0 } }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                This Month
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  justifyContent: "center",
                  mt: 1,
                  flexWrap: "wrap",
                  maxWidth: "100%",
                }}
              >
                {[
                  { label: "Total", status: "total" },
                  { label: "Success", status: "success" },
                  { label: "Failed", status: "failed" },
                ].map(({ label, status }) => (
                  <Button
                    key={status}
                    variant="outlined"
                    size="small"
                    onClick={() => handleDownload("thisMonth", status)}
                    sx={{
                      minWidth: 80,
                      whiteSpace: "nowrap",
                      fontSize: 14,
                      px: 2,
                      py: 0.5,
                    }}
                  >
                    {label}: {stats.thisMonth[status]}
                  </Button>
                ))}
              </Box>
            </Box>
            <Typography
              variant="h4"
              sx={{
                color: "grey.400",
                mx: 2,
                alignSelf: "center",
                display: { xs: "none", sm: "block" },
              }}
            >
              |
            </Typography>
            {/* Previous Month */}
            <Box sx={{ minWidth: 220, textAlign: "center" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Previous Month
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  justifyContent: "center",
                  mt: 1,
                  flexWrap: "wrap",
                  maxWidth: "100%",
                }}
              >
                {[
                  { label: "Total", status: "total" },
                  { label: "Success", status: "success" },
                  { label: "Failed", status: "failed" },
                ].map(({ label, status }) => (
                  <Button
                    key={status}
                    variant="outlined"
                    size="small"
                    onClick={() => handleDownload("prevMonth", status)}
                    sx={{
                      minWidth: 80,
                      whiteSpace: "nowrap",
                      fontSize: 14,
                      px: 2,
                      py: 0.5,
                    }}
                  >
                    {label}: {stats.prevMonth[status]}
                  </Button>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Paper>
      <Paper
        elevation={3}
        sx={{
          flex: 1,
          minWidth: 320,
          maxWidth: 1500,
          width: "100%",
          p: 4,
          mx: "auto",
          mb: 4,
        }}
      >
        <Typography variant="h4" gutterBottom align="center">
          Single MDN Change
        </Typography>
        <form
          onSubmit={handleSubmit}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <TextField
            label="Old Phone Number"
            value={oldPhoneNumber}
            onChange={(e) =>
              setOldPhoneNumber(e.target.value.replace(/\D/g, "").slice(0, 11))
            }
            fullWidth
            required
            margin="normal"
            sx={{ maxWidth: 400 }}
            slotProps={{ input: { maxLength: 11 } }}
          />
          <TextField
            label="Zip Code"
            value={zip}
            onChange={(e) =>
              setZip(e.target.value.replace(/\D/g, "").slice(0, 5))
            }
            fullWidth
            required={!zipOptional}
            margin="normal"
            disabled={zipOptional}
            sx={{ maxWidth: 400 }}
            slotProps={{ input: { maxLength: 5 } }}
          />
          <Box sx={{ mt: 1, width: "100%", maxWidth: 400, textAlign: "left" }}>
            <label>
              <input
                type="checkbox"
                checked={zipOptional}
                onChange={(e) => setZipOptional(e.target.checked)}
                style={{ marginRight: 8 }}
              />
              Make Zip Optional
            </label>
          </Box>
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ minWidth: 120 }}
            >
              {loading ? "Processing..." : "Submit"}
            </Button>
          </Box>
        </form>
        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
        {response && showResponse && (
          <Box sx={{ mt: 4 }} ref={responseRef}>
            <Typography variant="h6">Raw API Response:</Typography>
            <Paper sx={{ padding: 2, backgroundColor: "#f5f5f5", mt: 1 }}>
              <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {JSON.stringify(response, null, 2)}
              </pre>
            </Paper>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default SingleMdnChange;
